import React, { useMemo, useRef, useEffect, useState } from 'react';
import './RaceSelector.css';

function RaceSelector({ gameMode, race, favorites, onSetFavorite, onSetMultipleFavorites, onRemoveFavorite, isDropdownOpen, onOpenDropdown, invalidRiders = [], customRiders = [], allRiders = {}, competitions = [], apiUrl = '', onShowHistory = () => { }, onShowTop20 = () => { }, allFavoriteRiders = {}, onCopyFavorites = () => { }, onOpenNotes = () => { }, notes = {} }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [targetIndex, setTargetIndex] = useState(null);
  const [touchPos, setTouchPos] = useState({ x: 0, y: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('price'); // 'price', 'klassement', 'klimmen', etc.
  const [isCopyMenuOpen, setIsCopyMenuOpen] = useState(false);
  const [activeRiderName, setActiveRiderName] = useState(null);
  const dropdownRef = useRef(null);
  const nameRef = useRef(null);
  const longPressTimer = useRef(null);
  const initialTouchPos = useRef({ x: 0, y: 0 });
  const ghostElementRef = useRef(null);
  const containerRef = useRef(null);
  const isTouchDraggingRef = useRef(false);
  const listRef = useRef(null);

  // Scroll to top when sorting changes
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [sortBy]);

  // Reset sort when game mode changes
  useEffect(() => {
    setSortBy('price');
  }, [gameMode]);

  // Prevent scroll during touch drag using a non-passive listener
  // We use a REF here to bypass React's asynchronous state update latency
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onTouchMoveGlobal = (e) => {
      // Check the REF directly for zero-latency response
      if (isTouchDraggingRef.current) {
        if (e.cancelable) e.preventDefault();
      }
    };

    container.addEventListener('touchmove', onTouchMoveGlobal, { passive: false });
    return () => {
      container.removeEventListener('touchmove', onTouchMoveGlobal);
    };
  }, []); // Persistent listener on the main container ensures it's always active

  // Derive riders from global allRiders + customRiders
  const riders = useMemo(() => {
    // 1. Filter global riders for this specific race
    let raceRiders = [];
    if (allRiders) {
      raceRiders = Object.entries(allRiders)
        .filter(([name, data]) => {
          // Check if this rider participates in the current race
          return data.races && Array.isArray(data.races) && data.races.includes(race);
        })
        .map(([name, data]) => ({
          rider: name,
          price: data.price,
          riderId: data.RiderId,
          teamname: data.teamname,
          team_abbr: data.team_abbr,
          // Include qualities for sorting
          klassement: data.klassement || 0,
          klimmen: data.klimmen || 0,
          tijdrijden: data.tijdrijden || 0,
          sprinten: data.sprinten || 0,
          punchen: data.punchen || 0,
          heuvels: data.heuvels || 0,
          kasseien: data.kasseien || 0,
          nationaliteit: data.nationaliteit || '',
          type: data.type || '',
          birth: data.birth || '',
          procyclingstats_url: data.procyclingstats_url || null
        }));
    }

    // 2. Clone to avoid mutation
    const merged = [...raceRiders];

    // 3. Add custom riders if they are not already in the list
    customRiders.forEach(cr => {
      // Check if rider already exists in the filtered list
      const exists = merged.some(r => r.rider === cr.rider);
      if (!exists) {
        // Look up in global riders list to get correct price if available
        const globalRider = allRiders && allRiders[cr.rider];
        const price = globalRider ? globalRider.price : (cr.price || 500000);
        const riderId = globalRider ? globalRider.RiderId : null;
        const team_abbr = globalRider ? globalRider.team_abbr : null;
        const procyclingstats_url = globalRider ? globalRider.procyclingstats_url : (cr.procyclingstats_url || cr.procyclingstatsUrl || null);
        const qualities = globalRider ? {
          klassement: globalRider.klassement || 0,
          klimmen: globalRider.klimmen || 0,
          tijdrijden: globalRider.tijdrijden || 0,
          sprinten: globalRider.sprinten || 0,
          punchen: globalRider.punchen || 0,
          heuvels: globalRider.heuvels || 0,
          kasseien: globalRider.kasseien || 0,
          nationaliteit: globalRider.nationaliteit || '',
          type: globalRider.type || '',
          birth: globalRider.birth || ''
        } : {};

        merged.push({ rider: cr.rider, price, riderId, team_abbr, procyclingstats_url, ...qualities });
      }
    });

    // 4. Quality Filtering: If sorting by a quality, hide riders with 0 in that quality
    let filteredList = merged;
    const qualityKeys = ['klassement', 'klimmen', 'tijdrijden', 'sprinten', 'heuvels', 'punchen', 'kasseien'];
    if (qualityKeys.includes(sortBy)) {
      filteredList = merged.filter(r => (r[sortBy] || 0) > 0);
    }

    // 5. Sort based on sortBy
    filteredList.sort((a, b) => {
      if (sortBy === 'price') {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        if (priceB !== priceA) return priceB - priceA;
      } else {
        const valA = a[sortBy] || 0;
        const valB = b[sortBy] || 0;
        if (valB !== valA) return valB - valA;
        // Secondary: price
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        if (priceB !== priceA) return priceB - priceA;
      }

      const nameA = a.rider || '';
      const nameB = b.rider || '';
      return nameA.localeCompare(nameB);
    });

    // 6. Apply Search Filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      return filteredList.filter(r =>
        r.rider.toLowerCase().includes(term) ||
        (r.teamname && r.teamname.toLowerCase().includes(term)) ||
        (r.team_abbr && r.team_abbr.toLowerCase().includes(term))
      );
    }

    return filteredList;
  }, [allRiders, customRiders, race, searchTerm, sortBy]);

  // Click outside handler: sluit bij klik op overlay of drag-and-drop menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if any modal is currently open in the app
      const isAnyModalOpen = document.querySelector('.modal-box') !== null;
      if (isAnyModalOpen) return;

      // Close riders dropdown
      if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onOpenDropdown(null);
        setSearchTerm('');
        setActiveRiderName(null);
      }

      // Close copy menu
      if (isCopyMenuOpen) {
        const copyBtn = document.querySelector(`[data-race-copy="${race}"]`);
        const copyMenu = document.querySelector(`[data-race-copy-menu="${race}"]`);
        if (copyBtn && !copyBtn.contains(event.target) && copyMenu && !copyMenu.contains(event.target)) {
          setIsCopyMenuOpen(false);
        }
      }
    };

    if (isDropdownOpen || isCopyMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen, onOpenDropdown, isCopyMenuOpen, race]);

  const selectedRiders = (favorites || []).slice().sort((a, b) => a.rank - b.rank);
  const maxRanks = gameMode !== 'klassiekers' ? 20 : 15;
  const canAddMore = selectedRiders.length < maxRanks;
  const canRemove = selectedRiders.length > 0;

  // Group other races with same favorites
  const groupedOtherRaces = useMemo(() => {
    const groups = {}; // signature -> array of race names
    Object.entries(allFavoriteRiders).forEach(([r, list]) => {
      if (r === race || !list || list.length === 0) return;

      // Create unique signature for the favorite list
      const signature = list
        .slice()
        .sort((a, b) => a.rank - b.rank)
        .map(f => `${f.rider}:${f.rank}`)
        .join('|');

      if (!groups[signature]) groups[signature] = [];
      groups[signature].push(r);
    });

    return Object.values(groups).map(names => {
      // Natural sort for etappe names within the group
      names.sort((a, b) => {
        const numA = parseInt(a.match(/\d+/)?.[0] || 0, 10);
        const numB = parseInt(b.match(/\d+/)?.[0] || 0, 10);
        if (numA && numB) return numA - numB;
        return a.localeCompare(b);
      });
      return names;
    })
      .sort((groupA, groupB) => {
        // Sort groups by the first race in each group
        const a = groupA[0];
        const b = groupB[0];
        const numA = parseInt(a.match(/\d+/)?.[0] || 0, 10);
        const numB = parseInt(b.match(/\d+/)?.[0] || 0, 10);
        if (numA && numB) return numA - numB;
        return a.localeCompare(b);
      })
      .map(names => {
        let label = "";
        // Check if all names start with "Etappe " to use short format
        const isEveryEtappe = names.every(n => n.startsWith("Etappe "));

        if (isEveryEtappe && names.length > 1) {
          const nums = names.map(n => n.replace("Etappe ", "").trim());
          const head = nums.slice(0, -1).join(', ');
          const tail = nums[nums.length - 1];
          label = `Etappe ${head} en ${tail}`;
        } else {
          // Fallback or long format
          if (names.length === 1) {
            const raceName = names[0];
            const description = gameMode !== 'klassiekers' ? notes[`desc_${raceName}`] : null;
            label = description ? `${raceName}: ${description}` : raceName;
          } else if (names.length === 2) {
            label = `${names[0]} en ${names[1]}`;
          } else {
            label = `${names.slice(0, -1).join(', ')} en ${names[names.length - 1]}`;
          }
        }

        return {
          label,
          sourceRace: names[0] // They are identical, so any will do
        };
      });
  }, [allFavoriteRiders, race, gameMode, notes]);

  const openRiderLink = (event, url) => {
    if (!url) return;
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleToggleRider = (rider, price) => {
    const existing = (favorites || []).find(f => f.rider === rider);
    if (existing) {
      // Remove if already selected and more than 1
      if (canRemove) {
        // Remove the rider
        onRemoveFavorite(race, rider);
      }
    } else {
      // Add if not at max
      if (canAddMore) {
        const nextRank = Math.max(...(favorites || []).map(f => f.rank), 0) + 1;
        onSetFavorite(race, rider, nextRank);
      }
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    onOpenDropdown(null); // Sluit dropdown direct bij drag
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newFavorites = selectedRiders.slice();
    const [draggedItem] = newFavorites.splice(draggedIndex, 1);
    newFavorites.splice(targetIndex, 0, draggedItem);

    // Re-rank: assign ranks 1 to length
    const reranked = newFavorites.map((item, idx) => ({ rider: item.rider, rank: idx + 1 }));

    // Update via parent
    reranked.forEach(item => {
      onSetFavorite(race, item.rider, item.rank);
    });

    setDraggedIndex(null);
    onOpenDropdown(null); // Sluit dropdown na drag
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    initialTouchPos.current = { x: touch.clientX, y: touch.clientY };
    setTouchPos({ x: touch.clientX, y: touch.clientY });
    setActiveRiderName(selectedRiders[index].rider);

    // Start long press timer
    longPressTimer.current = setTimeout(() => {
      isTouchDraggingRef.current = true; // Sync ref update for immediate scroll lock
      setDraggedIndex(index);
      setTargetIndex(index);
      setIsTouchDragging(true);
      if (window.navigator.vibrate) {
        window.navigator.vibrate(50); // Haptic feedback
      }
    }, 400); // 400ms for long press
  };

  const handleTouchMove = (e) => {
    if (!longPressTimer.current && !isTouchDraggingRef.current) return;

    // Prevent default browser behavior (scroll) if we are in the 'potential' or 'active' drag state
    if (isTouchDraggingRef.current && e.cancelable) {
      e.preventDefault();
    }

    const touch = e.touches[0];
    const moveX = Math.abs(touch.clientX - initialTouchPos.current.x);
    const moveY = Math.abs(touch.clientY - initialTouchPos.current.y);

    // If moved significantly before long press, cancel timer
    // Threshold kept at 25px for better success rate on first try
    if (!isTouchDraggingRef.current && (moveX > 25 || moveY > 25)) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      return;
    }

    if (isTouchDraggingRef.current) {
      if (e.cancelable) e.preventDefault();
      setTouchPos({ x: touch.clientX, y: touch.clientY });

      // Find target index without styling it yet
      let elementOver;
      if (ghostElementRef.current) {
        const originalDisplay = ghostElementRef.current.style.display;
        ghostElementRef.current.style.display = 'none';
        elementOver = document.elementFromPoint(touch.clientX, touch.clientY);
        ghostElementRef.current.style.display = originalDisplay;
      } else {
        elementOver = document.elementFromPoint(touch.clientX, touch.clientY);
      }

      const riderItem = elementOver?.closest('.draggable-rider');
      if (riderItem) {
        const idx = parseInt(riderItem.getAttribute('data-index'), 10);
        if (!isNaN(idx)) {
          setTargetIndex(idx);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;

    if (isTouchDraggingRef.current && draggedIndex !== null && targetIndex !== null && draggedIndex !== targetIndex) {
      const newFavorites = selectedRiders.slice();
      const [draggedItem] = newFavorites.splice(draggedIndex, 1);
      newFavorites.splice(targetIndex, 0, draggedItem);

      const reranked = newFavorites.map((item, idx) => ({ rider: item.rider, rank: idx + 1 }));
      if (onSetMultipleFavorites) {
        onSetMultipleFavorites(race, reranked);
      } else {
        reranked.forEach(item => {
          onSetFavorite(race, item.rider, item.rank);
        });
      }
    }

    isTouchDraggingRef.current = false;
    setIsTouchDragging(false);
    setDraggedIndex(null);
    setTargetIndex(null);
  };

  const handleTouchCancel = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    isTouchDraggingRef.current = false;
    setIsTouchDragging(false);
    setDraggedIndex(null);
    setTargetIndex(null);
    setActiveRiderName(null);
  };

  const hasDesc = gameMode !== 'klassiekers' && notes[`desc_${race}`];

  return (
    <div className="race-selector" ref={containerRef}>
      {isDropdownOpen && (
        <div className="dropdown-overlay" onMouseDown={e => {
          // Overlay sluit alleen als je NIET op een dropdown-button klikt
          if (!e.target.closest('.dropdown-button')) {
            onOpenDropdown(null);
            setSearchTerm('');
          }
        }} />
      )}
      <div className="race-header">
        <div
          className="race-name-container clickable"
          onClick={() => onOpenNotes && onOpenNotes(race)}
        >
          <h3 className="race-name" ref={nameRef}>{race}</h3>
          {gameMode !== 'klassiekers' && notes[`desc_${race}`] && (
            <span className="race-description" title={notes[`desc_${race}`]}>
              {notes[`desc_${race}`]}
            </span>
          )}
        </div>
        {onOpenNotes && (
          <button
            className={`notes-race-icon-btn ${hasDesc ? 'with-desc' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenNotes(race);
            }}
            title="Notities voor deze wedstrijd"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
        )}
        <div className="race-menu-container">
          {onShowTop20 && gameMode === 'klassiekers' && (
            <button
              className="race-hamburger-btn"
              onClick={() => {
                const comp = competitions.find(c => c.TranslatedName === race);
                if (comp) {
                  onShowTop20(race, comp.CompetitionId);
                }
              }}
              title="Bekijk Top 20 uitslagen"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}

          {gameMode !== 'klassiekers' && (
            <div className="copy-menu-wrapper">
              <button
                className={`race-copy-btn ${isCopyMenuOpen ? 'active' : ''}`}
                data-race-copy={race}
                onClick={() => setIsCopyMenuOpen(!isCopyMenuOpen)}
                title="Kopieer favorieten van een andere etappe"
              >
                <span className="copy-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 15H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
                    <rect x="9" y="9" width="11" height="12" rx="2" />
                    <line x1="14.5" y1="12" x2="14.5" y2="18" />
                    <line x1="11.5" y1="15" x2="17.5" y2="15" />
                  </svg>
                </span>
              </button>

              {isCopyMenuOpen && (
                <div className="copy-dropdown" data-race-copy-menu={race}>
                  <div className="copy-dropdown-header">Kopieer alles van:</div>
                  <div className="copy-dropdown-list">
                    {groupedOtherRaces.map((group, idx) => (
                      <button
                        key={idx}
                        className="copy-item"
                        onClick={() => {
                          onCopyFavorites(race, group.sourceRace);
                          setIsCopyMenuOpen(false);
                        }}
                      >
                        {group.label}
                      </button>
                    ))}
                    {groupedOtherRaces.length === 0 && (
                      <div className="copy-no-items">Geen etappes met data</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Wrapper for dropdown and drag-list (allows ref to contain both) */}
      <div ref={dropdownRef}>
        {/* Dropdown list with checkboxes */}
        <div className="riders-dropdown">
          <button
            className="dropdown-button"
            onMouseDown={e => e.stopPropagation()} // Voorkom dat overlay click blokkeert
            onClick={e => {
              e.stopPropagation();
              onOpenDropdown(isDropdownOpen ? null : race);
            }}
            aria-expanded={isDropdownOpen}
          >
            {isDropdownOpen
              ? (gameMode === 'klassiekers' ? '▼ Kopmannen' : '▼ Favorieten')
              : (gameMode === 'klassiekers' ? '▶ Kopmannen' : '▶ Favorieten')}
          </button>
          <div ref={listRef} className={`riders-list ${isDropdownOpen ? 'open' : 'closed'}`}>
            {isDropdownOpen && (
              <div className="dropdown-search-container">
                <div className="dropdown-search-box">
                  <input
                    type="text"
                    placeholder="Zoek renner of ploeg..."
                    className="dropdown-search-input"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onClick={e => e.stopPropagation()} // Prevent closing dropdown when clicking input
                    onMouseDown={e => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <button
                      className="dropdown-search-clear-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchTerm('');
                      }}
                      title="Zoekopdracht wissen"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {gameMode !== 'klassiekers' && (
                  <div className="dropdown-sort-container">
                    <div className={`dropdown-sort-tabs ${gameMode !== 'klassiekers' ? 'is-giro' : ''}`}>
                      <button
                        className={`sort-tab price-tab ${sortBy === 'price' ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setSortBy('price'); }}
                        title="Sorteer op prijs"
                      >
                        €
                      </button>
                      <>
                        <button
                          className={`sort-tab ${sortBy === 'klassement' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setSortBy('klassement'); }}
                          title="Klassement"
                        >
                          GC
                        </button>
                        <button
                          className={`sort-tab ${sortBy === 'klimmen' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setSortBy('klimmen'); }}
                          title="Klimmen"
                        >
                          MT
                        </button>
                        <button
                          className={`sort-tab ${sortBy === 'tijdrijden' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setSortBy('tijdrijden'); }}
                          title="Tijdrijden"
                        >
                          TT
                        </button>
                        <button
                          className={`sort-tab ${sortBy === 'sprinten' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setSortBy('sprinten'); }}
                          title="Sprinten"
                        >
                          SPR
                        </button>
                        <button
                          className={`sort-tab ${sortBy === 'heuvels' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setSortBy('heuvels'); }}
                          title="Heuvels"
                        >
                          HVL
                        </button>
                      </>
                    </div>
                  </div>
                )}
              </div>
            )}
            {riders.length > 0 ? (
              riders.map(item => {
                const rider = item.rider;
                const price = item.price || 500000;
                const isSelected = (favorites || []).some(f => f.rider === rider);
                const canSelect = !isSelected && selectedRiders.length < maxRanks;
                const canDeselect = isSelected && selectedRiders.length >= 1;
                return (
                  <div key={rider} className={`rider-checkbox-item ${isSelected ? 'selected' : ''}`}>
                    <label className={canSelect || canDeselect ? '' : 'disabled'}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleRider(rider, price)}
                        disabled={!canSelect && !canDeselect}
                      />
                      <span className="rider-info">
                        <span className="rider-name">
                          {item.procyclingstats_url ? (
                            <button
                              type="button"
                              className="rider-name-link name-text"
                              title="Open ProCyclingStats-profiel"
                              draggable={false}
                              onMouseDown={e => e.stopPropagation()}
                              onTouchStart={e => e.stopPropagation()}
                              onDragStart={e => e.preventDefault()}
                              onClick={e => openRiderLink(e, item.procyclingstats_url)}
                            >
                              {rider}
                            </button>
                          ) : (
                            <span className="name-text">{rider}</span>
                          )}
                          {['klassement', 'klimmen', 'tijdrijden', 'sprinten', 'heuvels', 'punchen', 'kasseien'].includes(sortBy) && (
                            <span className={`rider-quality-score ${gameMode !== 'klassiekers' && item[sortBy] === 0.1 ? 'quality-nb' : ''}`}>
                              {gameMode !== 'klassiekers' && item[sortBy] === 0.1 ? 'NB' : `${(item[sortBy] / 2).toFixed(1).replace('.0', '')} ★`}
                            </span>
                          )}
                        </span>
                        {!invalidRiders.includes(rider) && (
                          <button
                            className="rider-history-btn rider-history-dropdown-btn"
                            title="Bekijk historische uitslagen"
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              onShowHistory({
                                name: rider,
                                riderId: item.riderId || null,
                                team_abbr: item.team_abbr || null,
                                klassement: item.klassement || 0,
                                klimmen: item.klimmen || 0,
                                tijdrijden: item.tijdrijden || 0,
                                sprinten: item.sprinten || 0,
                                punchen: item.punchen || 0,
                                heuvels: item.heuvels || 0,
                                kasseien: item.kasseien || 0
                              });
                            }}
                          >
                            <span /><span /><span />
                          </button>
                        )}
                        <span className={`rider-price ${gameMode !== 'klassiekers' && price === 10000 ? 'price-red' : ''}`}>€ {(price / 1000000).toFixed(2)}M</span>
                      </span>
                    </label>
                  </div>
                );
              })
            ) : (
              <div className="no-search-results">Geen renners gevonden.</div>
            )}
          </div>
        </div>
        {/* Selected riders - draggable list */}
        {selectedRiders.length > 0 && (
          <div className="selected-riders" onClick={() => onOpenDropdown(null)}>
            <div className="selected-label">{gameMode === 'klassiekers' ? 'Favoriete kopmannen (rank via drag-drop):' : 'Favoriete renners (rank via drag-drop):'}</div>
            <div
              className="draggable-list"
              onClick={() => onOpenDropdown(null)}
              onTouchCancel={handleTouchCancel}
            >
              {selectedRiders.map((item, index) => (
                <div
                  key={item.rider}
                  className={`draggable-rider ${draggedIndex === index
                    ? (isTouchDragging ? 'touch-dragging-placeholder' : 'dragging-placeholder')
                    : ''
                    } ${invalidRiders.includes(item.rider) ? 'invalid' : ''} ${activeRiderName === item.rider ? 'active-highlight' : ''}`}
                  title={invalidRiders.includes(item.rider) ? "Deze renner staat niet meer op de startlijst!" : ""}
                  draggable={!isTouchDragging}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchCancel}
                  data-index={index}
                >
                  <span className="rider-rank">#{item.rank}</span>
                  <span className="rider-name">
                    {allRiders[item.rider]?.procyclingstats_url ? (
                      <button
                        type="button"
                        className="rider-name-link name-text"
                        title="Open ProCyclingStats-profiel"
                        draggable={false}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onDragStart={e => e.preventDefault()}
                        onClick={e => openRiderLink(e, allRiders[item.rider]?.procyclingstats_url)}
                      >
                        {item.rider}
                      </button>
                    ) : item.rider}
                  </span>
                  <div className="rider-actions">
                    {!invalidRiders.includes(item.rider) && (
                      <button
                        className="rider-history-btn"
                        title="Bekijk historische uitslagen"
                        onClick={e => {
                          e.stopPropagation();
                          const riderData = allRiders[item.rider];
                          const riderId = riderData?.RiderId ?? null;
                          const team_abbr = riderData?.team_abbr ?? null;
                          onShowHistory({ name: item.rider, riderId, team_abbr });
                        }}
                      >
                        <span /><span /><span />
                      </button>
                    )}
                    <button
                      className="remove-rider-btn"
                      title="Verwijder deze renner"
                      onClick={e => {
                        e.stopPropagation();
                        // Verwijder en hernummer (nu centraal afgehandeld in App.jsx)
                        if (canRemove) {
                          const isInvalid = invalidRiders.includes(item.rider);
                          onRemoveFavorite(race, item.rider, isInvalid);
                        }
                      }}
                    >
                      <span aria-label="Verwijder" role="img">❌</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Drag Ghost */}
            {isTouchDragging && draggedIndex !== null && selectedRiders[draggedIndex] && (
              <div
                ref={ghostElementRef}
                className="floating-touch-ghost"
                style={{
                  position: 'fixed',
                  left: touchPos.x,
                  top: touchPos.y,
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  zIndex: 2000,
                  width: '90%',
                  maxWidth: '300px'
                }}
              >
                <div className="draggable-rider ghost-active">
                  <span className="rider-rank">#{selectedRiders[draggedIndex].rank}</span>
                  <span className="rider-name">{selectedRiders[draggedIndex].rider}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(RaceSelector);
